/**
 * Tasks Module V2 - Advanced Task Management
 *
 * Features:
 * - Short alphanumeric IDs (5 chars) for voice interaction
 * - AI and User task ownership/assignment
 * - Task dependencies with automatic status propagation
 * - Four views: List, Board (Kanban), Graph, Timeline
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useStatusWebSocket } from '@/hooks/useStatusWebSocket';
import {
  getTasks,
  getTaskStats,
  getTaskGraph,
  deleteTask,
  reorderTask,
  addTaskDependency,
  removeTaskDependency,
  updateTaskPositions,
  archiveTask,
  unarchiveTask,
  getArchivedTasks,
  autoArchiveTasks,
} from '@/services/api';
import type {
  Task,
  TaskStats,
  TasksFilter,
  TaskStatus,
  TaskActor,
  TaskViewMode,
} from '@/types/tasks';
import {
  TASK_STATUS_CONFIG,
  TASK_PRIORITY_CONFIG,
  TASK_ACTOR_CONFIG,
  groupTasksByStatus,
  getActiveKanbanColumns,
  isTaskOverdue,
  isTaskDueToday,
  formatDuration,
} from '@/types/tasks';
import type { TaskProject } from '@/types/tasks';
import { taskProjectsApi } from '@/services/taskProjectsApi';
import { useTenant } from '@/contexts/TenantContext';
import UserAvatar from '@/components/UserAvatar';

// React Flow imports
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  Connection,
  NodeProps,
  Handle,
  Position,
  BackgroundVariant,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// =============================================================================
// Task Comparison Helper
// =============================================================================

/**
 * Compare tasks to detect actual changes (returns true if different).
 * Used to prevent unnecessary re-renders and preserve scroll position.
 */
const tasksHaveChanged = (oldTasks: Task[], newTasks: Task[]): boolean => {
  if (oldTasks.length !== newTasks.length) return true;

  const oldMap = new Map(oldTasks.map(t => [t.id, t]));
  for (const newTask of newTasks) {
    const oldTask = oldMap.get(newTask.id);
    if (!oldTask) return true;
    // Compare updated_at timestamp to detect changes
    if (oldTask.updated_at !== newTask.updated_at) return true;
  }
  return false;
};

// =============================================================================
// Auto-Layout Helper
// =============================================================================

const NODE_WIDTH = 220;
const NODE_HEIGHT = 100;
const HORIZONTAL_GAP = 80;
const VERTICAL_GAP = 60;

/**
 * Auto-layout nodes that are at default position (0,0).
 * Uses a hierarchical layout based on dependencies.
 */
function autoLayoutNodes(nodes: Node[], edges: Edge[]): Node[] {
  if (nodes.length === 0) return nodes;

  // Check if any nodes need auto-layout (all at 0,0 or very close)
  const needsLayout = nodes.every(
    n => Math.abs(n.position.x) < 10 && Math.abs(n.position.y) < 10
  );

  if (!needsLayout) return nodes;

  // Build dependency graph
  const dependsOn = new Map<string, Set<string>>(); // task -> tasks it depends on
  const dependedBy = new Map<string, Set<string>>(); // task -> tasks that depend on it

  nodes.forEach(n => {
    dependsOn.set(n.id, new Set());
    dependedBy.set(n.id, new Set());
  });

  edges.forEach(e => {
    // Edge goes from source (dependency) to target (dependent)
    dependsOn.get(e.target)?.add(e.source);
    dependedBy.get(e.source)?.add(e.target);
  });

  // Calculate depth level for each node (topological sort)
  const levels = new Map<string, number>();
  const visited = new Set<string>();

  function calculateLevel(nodeId: string): number {
    if (levels.has(nodeId)) return levels.get(nodeId)!;
    if (visited.has(nodeId)) return 0; // Cycle detected

    visited.add(nodeId);

    const deps = dependsOn.get(nodeId) || new Set();
    if (deps.size === 0) {
      levels.set(nodeId, 0);
      return 0;
    }

    let maxDepLevel = 0;
    deps.forEach(depId => {
      maxDepLevel = Math.max(maxDepLevel, calculateLevel(depId) + 1);
    });

    levels.set(nodeId, maxDepLevel);
    return maxDepLevel;
  }

  nodes.forEach(n => calculateLevel(n.id));

  // Group nodes by level
  const nodesByLevel = new Map<number, Node[]>();
  nodes.forEach(n => {
    const level = levels.get(n.id) || 0;
    if (!nodesByLevel.has(level)) nodesByLevel.set(level, []);
    nodesByLevel.get(level)!.push(n);
  });

  // Position nodes
  const maxLevel = Math.max(...Array.from(levels.values()), 0);
  const positionedNodes: Node[] = [];

  for (let level = 0; level <= maxLevel; level++) {
    const levelNodes = nodesByLevel.get(level) || [];
    const levelWidth = levelNodes.length * (NODE_WIDTH + HORIZONTAL_GAP) - HORIZONTAL_GAP;
    const startX = -levelWidth / 2;

    levelNodes.forEach((node, index) => {
      positionedNodes.push({
        ...node,
        position: {
          x: startX + index * (NODE_WIDTH + HORIZONTAL_GAP),
          y: level * (NODE_HEIGHT + VERTICAL_GAP),
        },
      });
    });
  }

  return positionedNodes;
}

// =============================================================================
// Sub-Components
// =============================================================================

// Task Card for List View
const TaskListCard: React.FC<{
  task: Task;
  onDelete: (taskId: string) => void;
  onArchive?: (taskId: string) => void;
}> = ({ task, onDelete, onArchive }) => {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const statusConfig = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.pending;
  const priorityConfig = TASK_PRIORITY_CONFIG[task.priority] || TASK_PRIORITY_CONFIG.medium;
  const actorConfig = (task.assigned_to && TASK_ACTOR_CONFIG[task.assigned_to]) || TASK_ACTOR_CONFIG.user;
  const overdue = isTaskOverdue(task);
  const dueToday = isTaskDueToday(task);

  return (
    <div
      className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
        overdue ? 'border-red-300' : 'border-gray-200'
      }`}
      onClick={() => navigate(`/tasks/${task.id}`, { state: { task } })}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
              {task.id}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${actorConfig.bgColor} ${actorConfig.color}`}>
              {actorConfig.icon} {t(`actor.${task.assigned_to || 'user'}`)}
            </span>
            {task.created_by === 'ai' && task.assigned_to === 'user' && (
              <span className="text-xs text-purple-600">{t('list.card.fromAI')}</span>
            )}
          </div>

          {/* Title */}
          <h4 className="font-medium text-gray-900 mb-1 truncate">{task.title}</h4>

          {/* Description */}
          {task.description && (
            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{task.description}</p>
          )}

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className={`px-2 py-0.5 rounded-full ${statusConfig.bgColor} ${statusConfig.color}`}>
              {statusConfig.icon} {t(`status.${task.status}`)}
            </span>
            <span className={`px-2 py-0.5 rounded-full ${priorityConfig.bgColor} ${priorityConfig.color}`}>
              {t(`priority.${task.priority}`)}
            </span>
            {task.due_date && (
              <span className={`${overdue ? 'text-red-600 font-medium' : dueToday ? 'text-orange-600' : 'text-gray-500'}`}>
                {overdue ? t('list.card.overdue') : dueToday ? t('list.card.today') : t('list.card.due')}
                {new Date(task.due_date).toLocaleDateString()}
              </span>
            )}
            {task.estimated_minutes && (
              <span className="text-gray-500">{t('list.card.estimate', { duration: formatDuration(task.estimated_minutes) })}</span>
            )}
            {task.is_blocked && (
              <span className="text-red-600">{t('list.card.blocked', { count: task.blocking_tasks?.length })}</span>
            )}
            {task.project_name && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-gray-600" style={{ backgroundColor: `${task.project_color}20` }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: task.project_color || '#3B82F6' }} />
                {task.project_name}
              </span>
            )}
            {task.assigned_user_full_name && task.assigned_to_user_id && (
              <span className="text-gray-500 flex items-center gap-1">
                <UserAvatar
                  user={{
                    id: task.assigned_to_user_id,
                    full_name: task.assigned_user_full_name,
                    avatar_url: task.assigned_user_avatar_url,
                  }}
                  size="sm"
                />
                {task.assigned_user_full_name}
              </span>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-col gap-1">
          {/* Quick actions only for active tasks (not completed, blocked, or archived) */}
          {task.status !== 'completed' && task.status !== 'blocked' && task.status !== 'archived' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Note: Status changes now happen via iterations, not direct task update
                // This opens the task detail where user can manage iterations
                window.location.href = `/tasks/${task.id}`;
              }}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title={t('list.card.openTask')}
            >
              →
            </button>
          )}
          {/* Archive button for completed/blocked tasks (status is now computed) */}
          {(task.status === 'completed' || task.status === 'blocked') && onArchive && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onArchive(task.id);
              }}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              title={t('list.card.archive')}
            >
              📦
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(task.id);
            }}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title={t('list.card.delete')}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

// Task Card for Kanban Board
const KanbanCard: React.FC<{
  task: Task;
  isDragging: boolean;
  dragOverThis: boolean;
}> = ({ task, isDragging, dragOverThis }) => {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const priorityConfig = TASK_PRIORITY_CONFIG[task.priority] || TASK_PRIORITY_CONFIG.medium;
  const actorConfig = (task.assigned_to && TASK_ACTOR_CONFIG[task.assigned_to]) || TASK_ACTOR_CONFIG.user;
  const overdue = isTaskOverdue(task);

  return (
    <div
      className={`
        bg-white border rounded-lg p-3 cursor-pointer transition-all
        ${isDragging ? 'opacity-50 rotate-2' : ''}
        ${dragOverThis ? 'border-t-4 border-t-blue-500' : 'border-gray-200'}
        ${overdue ? 'border-l-4 border-l-red-500' : ''}
        hover:shadow-md
      `}
      onClick={() => navigate(`/tasks/${task.id}`, { state: { task } })}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="font-mono text-xs text-gray-400">{task.id}</span>
        <div className={`w-2 h-2 rounded-full ${priorityConfig.dotColor}`} title={t(`priority.${task.priority}`)} />
        <span className={`text-xs px-1.5 py-0.5 rounded ${actorConfig.bgColor} ${actorConfig.color}`}>
          {actorConfig.icon}
        </span>
      </div>
      <h4 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">{task.title}</h4>
      {task.due_date && (
        <p className={`text-xs ${overdue ? 'text-red-600' : 'text-gray-500'}`}>
          {new Date(task.due_date).toLocaleDateString()}
        </p>
      )}
      {task.is_blocked && (
        <p className="text-xs text-red-600 mt-1">🚫 Blocked</p>
      )}
      {/* Institutional: project dot + assigned user initial */}
      {(task.project_color || task.assigned_user_full_name) && (
        <div className="flex items-center gap-2 mt-1">
          {task.project_color && (
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: task.project_color }} title={task.project_name || ''} />
          )}
          {task.assigned_user_full_name && task.assigned_to_user_id && (
            <UserAvatar
              user={{
                id: task.assigned_to_user_id,
                full_name: task.assigned_user_full_name,
                avatar_url: task.assigned_user_avatar_url,
              }}
              size="sm"
            />
          )}
        </div>
      )}
    </div>
  );
};

// Custom Node for Graph View
const TaskGraphNode: React.FC<NodeProps> = ({ data, selected }) => {
  const { t } = useTranslation('tasks');
  const task = data?.task as Task | undefined;
  if (!task) return null;
  const statusConfig = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.pending;
  const priorityConfig = TASK_PRIORITY_CONFIG[task.priority] || TASK_PRIORITY_CONFIG.medium;
  const actorConfig = (task.assigned_to && TASK_ACTOR_CONFIG[task.assigned_to]) || TASK_ACTOR_CONFIG.user;

  return (
    <div
      className={`
        px-4 py-3 rounded-lg border-2 shadow-sm min-w-[180px] max-w-[220px]
        ${statusConfig.bgColor} ${statusConfig.borderColor}
        ${selected ? 'ring-2 ring-blue-500' : ''}
        transition-all
      `}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />

      <div className="flex items-center gap-2 mb-2">
        <div className={`w-2 h-2 rounded-full ${priorityConfig.dotColor}`} />
        <span className={`text-xs px-1.5 py-0.5 rounded ${actorConfig.bgColor} ${actorConfig.color}`}>
          {actorConfig.icon} {t(`actor.${task.assigned_to || 'user'}`)}
        </span>
        <span className="text-xs text-gray-400 ml-auto">{task.id}</span>
      </div>

      <h4 className="font-medium text-gray-900 text-sm truncate">{task.title}</h4>

      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
        <span className={statusConfig.color}>{t(`status.${task.status}`)}</span>
        {task.due_date && (
          <span>📅 {new Date(task.due_date).toLocaleDateString()}</span>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />
    </div>
  );
};

const nodeTypes = { task: TaskGraphNode };

// =============================================================================
// Main Component
// =============================================================================

const TasksModuleV2: React.FC = () => {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const location = useLocation();

  // Real-time status WebSocket
  const { lastTaskUpdate } = useStatusWebSocket();

  // State
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // View state
  const [viewMode, setViewMode] = useState<TaskViewMode>(() => {
    return (localStorage.getItem('tasksViewMode') as TaskViewMode) || 'board';
  });

  // Filter state
  const [filters] = useState<TasksFilter>({
    include_dependencies: true,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string>('');

  // Institutional features
  const { tenant } = useTenant();
  const isInstitutional = tenant?.tenant_type === 'institutional';
  const [projects, setProjects] = useState<TaskProject[]>([]);

  // Load projects for institutional tenants
  useEffect(() => {
    if (!isInstitutional) return;
    taskProjectsApi.list().then(setProjects).catch(console.error);
  }, [isInstitutional]);

  // Modal state (no longer used for create - using full-screen view instead)

  // Archive state
  const [showArchived, setShowArchived] = useState(false);
  const [archivedTasks, setArchivedTasks] = useState<Task[]>([]);
  const [archiveLoading, setArchiveLoading] = useState(false);

  // Kanban drag state
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<TaskStatus | null>(null);
  const [dragOverTask, setDragOverTask] = useState<string | null>(null);

  // Graph state
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // ==========================================================================
  // Data Loading
  // ==========================================================================

  const loadData = useCallback(async (background = false) => {
    // Only show loading indicator for foreground loads
    if (!background) {
      setLoading(true);
    }
    setError(null);
    try {
      const buildFilters: TasksFilter = {
        ...filters,
        include_dependencies: true,
      };
      if (searchQuery) buildFilters.search = searchQuery;
      if (statusFilter) buildFilters.status = statusFilter.split(',') as TaskStatus[];
      if (assigneeFilter) buildFilters.assigned_to = assigneeFilter as TaskActor;
      if (projectFilter) buildFilters.project_id = projectFilter;

      const [tasksData, statsData] = await Promise.all([
        getTasks(buildFilters),
        getTaskStats(),
      ]);

      // Only update tasks if data actually changed (preserves scroll position)
      setTasks(prev => {
        if (tasksHaveChanged(prev, tasksData)) {
          return tasksData;
        }
        return prev; // Keep same reference if no changes
      });
      setStats(statsData);

      // Load graph data if in graph view
      if (viewMode === 'graph') {
        const graphResponse = await getTaskGraph();

        // Auto-layout nodes that are at default position (0,0)
        const layoutedNodes = autoLayoutNodes(
          graphResponse.nodes as Node[],
          graphResponse.edges as Edge[]
        );

        setNodes(layoutedNodes);
        setEdges(graphResponse.edges as Edge[]);
      }
    } catch (err) {
      console.error('Failed to load tasks:', err);
      if (!background) {
        setError('Failed to load tasks');
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, [filters, searchQuery, statusFilter, assigneeFilter, projectFilter, viewMode, setNodes, setEdges]);

  // Initial data load
  useEffect(() => {
    loadData(false);
  }, [loadData]);

  // Background refresh when returning to /tasks (preserves scroll position)
  useEffect(() => {
    if (location.pathname === '/tasks' && tasks.length > 0) {
      // Already have data - do background refresh only
      loadData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);  // Intentionally exclude loadData and tasks to prevent loops

  // Persist view mode
  useEffect(() => {
    localStorage.setItem('tasksViewMode', viewMode);
  }, [viewMode]);

  // React to real-time task status updates from WebSocket
  useEffect(() => {
    if (lastTaskUpdate) {
      setTasks(prev => prev.map(task =>
        task.id === lastTaskUpdate.id
          ? { ...task, status: lastTaskUpdate.status as TaskStatus, updated_at: lastTaskUpdate.updated_at }
          : task
      ));
    }
  }, [lastTaskUpdate]);

  // ==========================================================================
  // Task Operations
  // ==========================================================================

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm(t('list.delete.confirm', { id: taskId }))) return;
    try {
      await deleteTask(taskId);
      await loadData();
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  };

  // Note: Status changes are now computed from iterations
  // Status cannot be changed directly - use iteration workflow instead

  // ==========================================================================
  // Archive Operations
  // ==========================================================================

  const handleArchiveTask = async (taskId: string) => {
    try {
      await archiveTask(taskId);
      await loadData();
      // If viewing archived, refresh that list too
      if (showArchived) {
        const archived = await getArchivedTasks();
        setArchivedTasks(archived);
      }
    } catch (err) {
      console.error('Failed to archive task:', err);
    }
  };

  const handleUnarchiveTask = async (taskId: string) => {
    try {
      await unarchiveTask(taskId);
      const archived = await getArchivedTasks();
      setArchivedTasks(archived);
      await loadData();
    } catch (err) {
      console.error('Failed to unarchive task:', err);
    }
  };

  const handleAutoArchive = async () => {
    if (!confirm(t('list.archive.confirmAutoArchive'))) return;
    setArchiveLoading(true);
    try {
      const result = await autoArchiveTasks({ completed_days: 30, cancelled_days: 7 });
      if (result.total_archived > 0) {
        await loadData();
        if (showArchived) {
          const archived = await getArchivedTasks();
          setArchivedTasks(archived);
        }
        alert(t('list.archive.successMessage', { total: result.total_archived, completed: result.completed_archived, cancelled: result.cancelled_archived }));
      } else {
        alert(t('list.archive.nothingToArchive'));
      }
    } catch (err) {
      console.error('Failed to auto-archive:', err);
    } finally {
      setArchiveLoading(false);
    }
  };

  const toggleShowArchived = async () => {
    if (!showArchived) {
      // Loading archived tasks
      setArchiveLoading(true);
      try {
        const archived = await getArchivedTasks();
        setArchivedTasks(archived);
      } catch (err) {
        console.error('Failed to load archived tasks:', err);
      } finally {
        setArchiveLoading(false);
      }
    }
    setShowArchived(!showArchived);
  };

  // ==========================================================================
  // Kanban Drag & Drop
  // ==========================================================================

  const handleDragStart = (e: React.DragEvent, task: Task) => {
    if (task.status === 'blocked') {
      e.preventDefault();
      return;
    }
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, status: TaskStatus) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(status);
  };

  const handleDragOverTask = (e: React.DragEvent, taskId: string) => {
    e.preventDefault();
    setDragOverTask(taskId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
    setDragOverTask(null);
  };

  const handleDrop = async (e: React.DragEvent, targetStatus: TaskStatus) => {
    e.preventDefault();
    if (!draggedTask) return;

    const targetTasks = tasks.filter(t => t.status === targetStatus && t.id !== draggedTask.id);
    let newPosition = targetTasks.length;

    if (dragOverTask) {
      const hoverIndex = targetTasks.findIndex(t => t.id === dragOverTask);
      if (hoverIndex !== -1) newPosition = hoverIndex;
    }

    try {
      await reorderTask(draggedTask.id, {
        new_status: targetStatus,
        new_position: newPosition,
      });
      await loadData();
    } catch (err) {
      console.error('Failed to reorder task:', err);
    }

    setDraggedTask(null);
    setDragOverColumn(null);
    setDragOverTask(null);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDragOverColumn(null);
    setDragOverTask(null);
  };

  // ==========================================================================
  // Graph Operations
  // ==========================================================================

  const handleGraphNodeDragStop = useCallback(async (_: any, node: Node) => {
    try {
      await updateTaskPositions({
        positions: [{ id: node.id, x: node.position.x, y: node.position.y }],
      });
    } catch (err) {
      console.error('Failed to save position:', err);
    }
  }, []);

  const handleGraphConnect = useCallback(async (connection: Connection) => {
    if (!connection.source || !connection.target) return;
    try {
      await addTaskDependency(connection.target, {
        depends_on_task_id: connection.source,
        dependency_type: 'blocks',
      });
      await loadData();
    } catch (err) {
      console.error('Failed to add dependency:', err);
      alert(t('list.graph.addDependencyError'));
    }
  }, [loadData]);

  const handleGraphEdgeClick = useCallback(async (_: any, edge: Edge) => {
    if (!confirm(t('list.graph.removeDependencyConfirm'))) return;
    try {
      const dep = (edge.data as any)?.dependency;
      if (dep) {
        await removeTaskDependency(dep.task_id, dep.id);
        await loadData();
      }
    } catch (err) {
      console.error('Failed to remove dependency:', err);
    }
  }, [loadData]);

  // ==========================================================================
  // Computed Values
  // ==========================================================================

  const tasksByStatus = useMemo(() => groupTasksByStatus(tasks), [tasks]);

  // Dynamic Kanban columns based on actual task statuses
  const activeColumns = useMemo(() => getActiveKanbanColumns(tasksByStatus), [tasksByStatus]);

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      if (!task) return false;
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        const title = task.title || '';
        const description = task.description || '';
        const id = task.id || '';
        if (!title.toLowerCase().includes(search) &&
            !description.toLowerCase().includes(search) &&
            !id.toLowerCase().includes(search)) {
          return false;
        }
      }
      return true;
    });
  }, [tasks, searchQuery]);

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="bg-gray-50" style={{ minHeight: 'calc(100vh - var(--health-banner-height, 0px))' }}>
      {/* Header - uses CSS variable from HealthBanner */}
      <div className="bg-white border-b border-gray-200 sticky z-30" style={{ top: 'var(--health-banner-height, 0px)' }}>
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/voice')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">{t('list.header.title')}</h1>
                <p className="text-xs md:text-sm text-gray-500">
                  {stats ? t('list.header.stats', { total: stats.total, inProgress: stats.by_status?.in_progress || 0 }) : t('list.header.statsLoading')}
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate('/tasks/new')}
              className="btn-primary text-sm px-3 md:px-4 py-2 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">{t('list.header.newTask')}</span>
            </button>
          </div>

          {/* View Mode Toggle & Filters */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {/* View Toggle */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              {(['list', 'board', 'graph', 'timeline'] as TaskViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    viewMode === mode
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {mode === 'list' && '📋'}
                  {mode === 'board' && '📊'}
                  {mode === 'graph' && '🔗'}
                  {mode === 'timeline' && '📅'}
                  <span className="ml-1 hidden sm:inline capitalize">{t(`list.views.${mode}`)}</span>
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1 min-w-[200px] max-w-md relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('list.search.placeholder')}
                className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filters */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">{t('list.filters.allStatus')}</option>
              {Object.keys(TASK_STATUS_CONFIG).map((key) => (
                <option key={key} value={key}>{t(`status.${key}`)}</option>
              ))}
            </select>

            <select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">{t('list.filters.allAssignees')}</option>
              <option value="user">{t('list.filters.assignedToYou')}</option>
              <option value="ai">{t('list.filters.assignedToAI')}</option>
            </select>

            {/* Project Filter (institutional only) */}
            {isInstitutional && projects.length > 0 && (
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All Projects</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.icon} {p.name}</option>
                ))}
              </select>
            )}

            {/* Archive Controls */}
            <div className="flex items-center gap-2 border-l border-gray-300 pl-3 ml-1">
              <button
                onClick={toggleShowArchived}
                disabled={archiveLoading}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1 ${
                  showArchived
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                📦 {showArchived ? t('list.archive.hide') : t('list.archive.show')} {t('list.archive.archived')}
                {stats?.archived ? ` (${stats.archived})` : ''}
              </button>
              <button
                onClick={handleAutoArchive}
                disabled={archiveLoading}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1"
                title={t('list.archive.autoArchiveTitle')}
              >
                {archiveLoading ? (
                  <div className="animate-spin h-4 w-4 border-2 border-gray-600 border-t-transparent rounded-full" />
                ) : (
                  '🗄️'
                )}
                <span className="hidden lg:inline">{t('list.archive.autoArchive')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 md:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        ) : (
          <>
            {/* Archived Tasks Panel */}
            {showArchived && (
              <div className="mb-6 bg-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                    📦 {t('list.archive.archivedTasks', { count: archivedTasks.length })}
                  </h3>
                  <button
                    onClick={() => setShowArchived(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {archiveLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin h-6 w-6 border-4 border-gray-600 border-t-transparent rounded-full" />
                  </div>
                ) : archivedTasks.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">{t('list.archive.noArchived')}</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {archivedTasks.map((task) => {
                      const statusConfig = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.pending;
                      return (
                      <div
                        key={task.id}
                        className="bg-white rounded-lg p-3 flex items-center justify-between border border-gray-200"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-gray-400">{task.id || 'N/A'}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${statusConfig.bgColor} ${statusConfig.color}`}>
                              {t(`status.${task.status}`)}
                            </span>
                          </div>
                          <h4 className="font-medium text-gray-700 truncate">{task.title || 'Untitled'}</h4>
                          {task.archived_at && (
                            <p className="text-xs text-gray-400">
                              {t('list.archive.archived_at', { date: new Date(task.archived_at).toLocaleDateString() })}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleUnarchiveTask(task.id)}
                          className="ml-3 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors"
                        >
                          {t('list.archive.restore')}
                        </button>
                      </div>
                    );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="max-w-4xl mx-auto space-y-3">
                {filteredTasks.length === 0 ? (
                  <EmptyState onCreateClick={() => navigate('/tasks/new')} />
                ) : (
                  filteredTasks.map((task) => (
                    <TaskListCard
                      key={task.id}
                      task={task}
                      onDelete={handleDeleteTask}
                      onArchive={handleArchiveTask}
                    />
                  ))
                )}
              </div>
            )}

            {/* Board View (Kanban) */}
            {viewMode === 'board' && (
              <div className="flex gap-4 overflow-x-auto pb-4">
                {activeColumns.map((status) => (
                  <div
                    key={status}
                    className="flex-shrink-0 w-72"
                    onDragOver={(e) => handleDragOver(e, status)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, status)}
                  >
                    {/* Column Header */}
                    <div className={`p-3 rounded-t-lg border-t border-x ${TASK_STATUS_CONFIG[status].bgColor} ${TASK_STATUS_CONFIG[status].borderColor}`}>
                      <div className="flex items-center justify-between">
                        <h3 className={`font-semibold ${TASK_STATUS_CONFIG[status].color}`}>
                          {TASK_STATUS_CONFIG[status].icon} {t(`status.${status}`)}
                        </h3>
                        <span className="text-sm text-gray-600 bg-white px-2 py-0.5 rounded-full">
                          {tasksByStatus[status].length}
                        </span>
                      </div>
                    </div>

                    {/* Column Content */}
                    <div
                      className={`
                        border-x border-b rounded-b-lg p-2 min-h-[400px] space-y-2
                        ${dragOverColumn === status ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'}
                      `}
                    >
                      {tasksByStatus[status].map((task) => (
                        <div
                          key={task.id}
                          draggable={task.status !== 'blocked'}
                          onDragStart={(e) => handleDragStart(e, task)}
                          onDragOver={(e) => handleDragOverTask(e, task.id)}
                          onDragEnd={handleDragEnd}
                        >
                          <KanbanCard
                            task={task}
                            isDragging={draggedTask?.id === task.id}
                            dragOverThis={dragOverTask === task.id}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Graph View */}
            {viewMode === 'graph' && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" style={{ height: '600px' }}>
                {tasks.length === 0 ? (
                  <EmptyState onCreateClick={() => navigate('/tasks/new')} />
                ) : (
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onNodeDragStop={handleGraphNodeDragStop}
                    onConnect={handleGraphConnect}
                    onEdgeClick={handleGraphEdgeClick}
                    nodeTypes={nodeTypes}
                    fitView
                    snapToGrid
                    snapGrid={[20, 20]}
                    defaultEdgeOptions={{
                      type: 'smoothstep',
                      markerEnd: { type: MarkerType.ArrowClosed },
                    }}
                  >
                    <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
                    <Controls />
                    <MiniMap
                      nodeColor={(node) => {
                        const task = (node.data as any)?.task as Task | undefined;
                        if (!task) return '#6b7280';
                        if (task.status === 'completed') return '#22c55e';
                        if (task.status === 'blocked') return '#ef4444';
                        if (task.status === 'in_progress') return '#eab308';
                        if (task.status === 'for_review') return '#6366f1';
                        if (task.status === 'waiting') return '#f97316';
                        if (task.status === 'draft') return '#94a3b8';
                        if (task.assigned_to === 'ai') return '#8b5cf6';
                        return '#6b7280';
                      }}
                    />
                  </ReactFlow>
                )}
                <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 text-xs text-gray-600 border border-gray-200">
                  <p className="font-medium mb-1">{t('list.graph.controls.title')}</p>
                  <p>{t('list.graph.controls.reposition')}</p>
                  <p>{t('list.graph.controls.createDependencies')}</p>
                  <p>{t('list.graph.controls.removeDependency')}</p>
                </div>
              </div>
            )}

            {/* Timeline View */}
            {viewMode === 'timeline' && (
              <TimelineView
                tasks={filteredTasks}
                onTaskClick={(task) => navigate(`/tasks/${task.id}`, { state: { task } })}
              />
            )}
          </>
        )}
      </div>

      {/* Task Detail Page renders here via nested route (including create mode at /tasks/new) */}
      <Outlet />
    </div>
  );
};

// =============================================================================
// Timeline View Component
// =============================================================================

const TimelineView: React.FC<{
  tasks: Task[];
  onTaskClick: (task: Task) => void;
}> = ({ tasks, onTaskClick }) => {
  const { t } = useTranslation('tasks');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get tasks with due dates, sorted
  const tasksWithDates = tasks
    .filter(t => t.due_date && t.status !== 'cancelled')
    .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime());

  // Group by date
  const tasksByDate: Record<string, Task[]> = {};
  tasksWithDates.forEach(task => {
    const dateKey = task.due_date!.split('T')[0];
    if (!tasksByDate[dateKey]) tasksByDate[dateKey] = [];
    tasksByDate[dateKey].push(task);
  });

  const dates = Object.keys(tasksByDate).sort();

  if (dates.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <p className="text-gray-500">{t('list.timeline.noTasks')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

        {/* Date groups */}
        {dates.map((dateKey) => {
          const date = new Date(dateKey);
          const isToday = date.getTime() === today.getTime();
          const isPast = date < today;
          const dateTasks = tasksByDate[dateKey];

          return (
            <div key={dateKey} className="relative pl-12 pb-6">
              {/* Date marker */}
              <div className={`
                absolute left-2 w-5 h-5 rounded-full border-2 border-white
                ${isToday ? 'bg-blue-500' : isPast ? 'bg-gray-400' : 'bg-gray-300'}
              `} />

              {/* Date label */}
              <div className={`font-medium mb-2 ${isToday ? 'text-blue-600' : isPast ? 'text-gray-500' : 'text-gray-700'}`}>
                {isToday ? t('list.timeline.today') : ''}
                {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </div>

              {/* Tasks for this date */}
              <div className="space-y-2">
                {dateTasks.map(task => {
                  const statusConfig = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.pending;
                  const actorConfig = (task.assigned_to && TASK_ACTOR_CONFIG[task.assigned_to]) || TASK_ACTOR_CONFIG.user;

                  return (
                    <div
                      key={task.id}
                      onClick={() => onTaskClick(task)}
                      className={`
                        p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow
                        ${statusConfig.bgColor} ${statusConfig.borderColor}
                      `}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs text-gray-400">{task.id}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${actorConfig.bgColor} ${actorConfig.color}`}>
                          {actorConfig.icon}
                        </span>
                        <span className={`text-xs ${statusConfig.color}`}>{t(`status.${task.status}`)}</span>
                      </div>
                      <h4 className="font-medium text-gray-900 text-sm">{task.title}</h4>
                      {task.estimated_minutes && (
                        <p className="text-xs text-gray-500 mt-1">⏱ {formatDuration(task.estimated_minutes)}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// Empty State
// =============================================================================

const EmptyState: React.FC<{ onCreateClick: () => void }> = ({ onCreateClick }) => {
  const { t } = useTranslation('tasks');
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
      <div className="text-5xl mb-4">{t('list.empty.icon')}</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">{t('list.empty.title')}</h3>
      <p className="text-gray-500 mb-4">{t('list.empty.description')}</p>
      <button onClick={onCreateClick} className="btn-primary">
        {t('list.empty.button')}
      </button>
    </div>
  );
};

export default TasksModuleV2;
